export const WIDGET_STYLES = `
:host {
  --mfb-accent: #3b82f6;
  --mfb-accent-contrast: #ffffff;

  /* FAB-specific palette — Mhosaic brand (steel blue family, per
     mhosaic-core's design system v4). Soft-black surface + light
     steel-blue glyph. Kept separate from --mfb-accent on purpose:
     the accent is used everywhere inside the panel for submit
     buttons / focus rings, and we don't want changing the FAB chrome
     to inadvertently restyle the whole widget. */
  --mfb-fab-bg: #1c2230;       /* Mhosaic --black (oklch ≈ 0.22 0.025 258) */
  --mfb-fab-icon: #8aa9e5;     /* Mhosaic --blue-300 (oklch ≈ 0.70 0.13 258) */
  --mfb-bg: #ffffff;
  --mfb-surface: #f9fafb;
  --mfb-surface-2: #f3f4f6;
  --mfb-text: #0a0a0a;
  --mfb-text-muted: #6b7280;
  --mfb-border: #e5e7eb;
  --mfb-border-strong: #d1d5db;
  --mfb-radius: 8px;
  --mfb-radius-lg: 14px;
  --mfb-font: system-ui, -apple-system, sans-serif;
  --mfb-z-index: 2147483640;

  /* Spacing scale — 4px base, used everywhere instead of magic numbers. */
  --mfb-space-1: 4px;
  --mfb-space-2: 8px;
  --mfb-space-3: 12px;
  --mfb-space-4: 16px;
  --mfb-space-5: 24px;
  --mfb-space-6: 32px;
  --mfb-space-7: 48px;

  /* Type scale — fixed sizes, no fluid scaling. Body 14px sits in the
     "comfortable on every viewport" range; headings step up gracefully. */
  --mfb-text-xs: 11px;
  --mfb-text-sm: 13px;
  --mfb-text-base: 14px;
  --mfb-text-md: 15px;
  --mfb-text-lg: 17px;
  --mfb-text-xl: 20px;

  /* Elevation — three tiers for layering. */
  --mfb-shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.08);
  --mfb-shadow-md: 0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.12);
  --mfb-shadow-lg: 0 24px 56px rgba(0, 0, 0, 0.18), 0 8px 20px rgba(0, 0, 0, 0.10);

  all: initial;
  font-family: var(--mfb-font);
  color: var(--mfb-text);
  position: fixed;
  z-index: var(--mfb-z-index);
}

@media (prefers-color-scheme: dark) {
  :host {
    --mfb-bg: #0f172a;
    --mfb-surface: #1e293b;
    --mfb-surface-2: #253349;
    --mfb-text: #f8fafc;
    --mfb-text-muted: #94a3b8;
    --mfb-border: #334155;
    --mfb-border-strong: #475569;
    --mfb-shadow-lg: 0 24px 56px rgba(0, 0, 0, 0.55), 0 8px 20px rgba(0, 0, 0, 0.40);
  }
}

/* FAB — 48px circle (down from Material's 56px) for a more discrete
   presence on client surfaces (matches PNR's 48px pattern). Mhosaic
   soft-black background with a light steel-blue lucide-bug glyph;
   custom SVG inlined (no emoji — emoji renders inconsistently across
   OSes and can't inherit color). Two-layer elevation, scale-on-press. */
.fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 48px;
  height: 48px;
  border-radius: 999px;
  background: var(--mfb-fab-bg);
  color: var(--mfb-fab-icon);
  border: none;
  cursor: pointer;
  /* Two-layer elevation: ambient (soft, large) + key (tighter, near). */
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.08),
    0 2px 4px rgba(0, 0, 0, 0.12);
  display: grid;
  place-items: center;
  transition: transform 180ms cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1);
}

.fab:hover {
  transform: translateY(-2px);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.12),
    0 3px 6px rgba(0, 0, 0, 0.16);
}
.fab:active {
  transform: translateY(0) scale(0.96);
  box-shadow:
    0 3px 8px rgba(0, 0, 0, 0.10),
    0 1px 2px rgba(0, 0, 0, 0.14);
}
.fab:focus-visible {
  outline: 2px solid var(--mfb-fab-icon);
  outline-offset: 3px;
  box-shadow:
    0 0 0 4px var(--mfb-fab-bg),
    0 4px 12px rgba(0, 0, 0, 0.08),
    0 2px 4px rgba(0, 0, 0, 0.12);
}
@media (prefers-color-scheme: dark) {
  /* Slightly desaturate so the FAB doesn't glow against dark backgrounds. */
  .fab { box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.32),
    0 2px 4px rgba(0, 0, 0, 0.40); }
}
@media (prefers-reduced-motion: reduce) {
  .fab { transition: none; }
  .fab:hover, .fab:active { transform: none; }
}

/* Backdrop — fade in with a slight blur for depth. The blur gives the
   modal that "page that opens" weight: the underlying page recedes
   visually so the widget feels foregrounded, not pasted on. */
.backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: grid;
  place-items: center;
  animation: mfb-backdrop-in 220ms cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes mfb-backdrop-in {
  from { opacity: 0; backdrop-filter: blur(0px); -webkit-backdrop-filter: blur(0px); }
  to { opacity: 1; backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); }
}

.modal {
  background: var(--mfb-bg);
  border-radius: var(--mfb-radius-lg);
  box-shadow: var(--mfb-shadow-lg);
  /* 720px is the "page that opens" sweet spot — wide enough to feel
     like a workspace, narrow enough to not dominate the screen. Was
     440px before; the bump trades a denser modal for a calmer canvas. */
  width: min(720px, calc(100vw - var(--mfb-space-7)));
  padding: var(--mfb-space-6);
  display: flex;
  flex-direction: column;
  gap: var(--mfb-space-5);
  position: relative;
  /* Cap height with breathing room. The form scrolls internally if
     the user attaches a tall screenshot. */
  max-height: min(820px, calc(100vh - var(--mfb-space-7)));
  overflow-y: auto;
  /* Entrance: opacity-only. Avoids any geometric shift during the
     animation so interaction tests that read boundingBox immediately
     after the modal becomes visible get stable coordinates. 220ms
     ease-out is snappy enough that human users barely register it. */
  animation: mfb-modal-in 220ms ease-out;
  /* Transition lives on the BASE rule with a SHORTER duration so the
     "back" trip (Board → other tab, where .is-expanded is removed)
     feels snappy. The grow direction overrides this with a slower
     curve in .modal.is-expanded below — the asymmetry is intentional:
     a slow grow reads as a transport, a slow shrink reads as
     hesitation. */
  transition:
    width 200ms cubic-bezier(0.4, 0, 0.2, 1),
    height 200ms cubic-bezier(0.4, 0, 0.2, 1),
    max-height 200ms cubic-bezier(0.4, 0, 0.2, 1),
    padding 200ms cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes mfb-modal-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Mobile: full-height sheet that slides up from the bottom. The
   slide-up gesture matches platform-native sheets users already know. */
@media (max-width: 640px) {
  .backdrop { place-items: end center; }
  .modal {
    width: 100vw;
    height: calc(100vh - var(--mfb-space-7));
    max-height: none;
    border-radius: var(--mfb-radius-lg) var(--mfb-radius-lg) 0 0;
    padding: var(--mfb-space-5) var(--mfb-space-4) var(--mfb-space-4);
    animation: mfb-sheet-up 280ms cubic-bezier(0.16, 1, 0.3, 1);
  }
}

@keyframes mfb-sheet-up {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

@media (prefers-reduced-motion: reduce) {
  .backdrop, .modal { animation: none; }
}

.modal h2 {
  margin: 0;
  font-size: var(--mfb-text-xl);
  font-weight: 600;
  padding-right: var(--mfb-space-6);
  letter-spacing: -0.015em;
  line-height: 1.2;
}

.modal form {
  display: flex;
  flex-direction: column;
  gap: var(--mfb-space-5);
  /* Match the entrance motion the Board/MineList/Changelog containers
   * use so switching tabs (especially Board → Send, where the modal
   * is mid-shrink) doesn't show a content snap behind the transition.
   * 40ms delay lets the modal's shrink finish first. */
  animation: mfb-board-in 220ms ease-out 40ms both;
}
@media (prefers-reduced-motion: reduce) {
  .modal form { animation: none; }
}

.modal-close {
  position: absolute;
  top: var(--mfb-space-3);
  right: var(--mfb-space-3);
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  background: transparent;
  border: none;
  border-radius: var(--mfb-radius);
  color: var(--mfb-text-muted);
  font: inherit;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;
}
.modal-close:hover { background: var(--mfb-surface); color: var(--mfb-text); }
.modal-close:focus-visible { outline: 2px solid var(--mfb-accent); outline-offset: 2px; }

/* Each field group: label + input stacked with breathing room.
   The 24px modal-level gap separates groups. */
.field { display: flex; flex-direction: column; gap: var(--mfb-space-2); font-size: var(--mfb-text-sm); }

.field label {
  color: var(--mfb-text-muted);
  font-weight: 500;
  font-size: var(--mfb-text-xs);
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.field input, .field select, .field textarea {
  font-family: inherit;
  font-size: var(--mfb-text-base);
  color: inherit;
  padding: 11px 14px;
  border: 1px solid var(--mfb-border);
  border-radius: var(--mfb-radius);
  background: var(--mfb-surface);
  transition: border-color 120ms ease, box-shadow 120ms ease, background 120ms ease;
}

.field input:hover, .field select:hover, .field textarea:hover { border-color: var(--mfb-border-strong); }
.field input:focus, .field select:focus, .field textarea:focus {
  outline: none;
  border-color: var(--mfb-accent);
  background: var(--mfb-bg);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--mfb-accent) 22%, transparent);
}

.field select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  padding-right: 28px;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='none' stroke='%236b7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' d='M1 1l4 4 4-4'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
}

.field textarea { min-height: 120px; resize: vertical; line-height: 1.5; }

.row { display: flex; gap: var(--mfb-space-3); }
.row > * { flex: 1; }

/* Footer: subtle separation via border, slightly more vertical space. */
.actions {
  display: flex;
  gap: var(--mfb-space-2);
  justify-content: flex-end;
  padding-top: var(--mfb-space-4);
  margin-top: var(--mfb-space-1);
  border-top: 1px solid var(--mfb-border);
}

.btn {
  padding: 10px 18px;
  border-radius: var(--mfb-radius);
  border: 1px solid var(--mfb-border);
  background: var(--mfb-bg);
  color: var(--mfb-text);
  font: inherit;
  font-size: var(--mfb-text-sm);
  font-weight: 500;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease, transform 80ms ease;
}
.btn:hover { background: var(--mfb-surface); border-color: var(--mfb-border-strong); }
.btn:active { transform: scale(0.98); }

.btn--primary {
  background: var(--mfb-accent);
  color: var(--mfb-accent-contrast);
  border-color: var(--mfb-accent);
}
.btn--primary:hover {
  background: color-mix(in srgb, var(--mfb-accent) 88%, black);
  border-color: color-mix(in srgb, var(--mfb-accent) 88%, black);
}

/* Subdued button — borrows accent text but transparent background. Used
 * for the "Capture this page" alt-action that sits below the dropzone,
 * where a full --primary would compete with the form's main submit. */
.btn--ghost {
  background: transparent;
  color: var(--mfb-accent);
  border-color: color-mix(in srgb, var(--mfb-accent) 40%, transparent);
}
.btn--ghost:hover {
  background: color-mix(in srgb, var(--mfb-accent) 8%, transparent);
  border-color: var(--mfb-accent);
}

.btn[disabled] { opacity: 0.6; cursor: not-allowed; }

.error { color: #dc2626; font-size: 13px; }
.success { color: #059669; font-size: 13px; }

/* ---- v0.6.0: manual screenshot upload + annotator -------------------- */

.mfb-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Dropzone — bigger, more inviting; the icon + heading + sub-line
   hierarchy makes it feel like a deliberate target, not an afterthought. */
.screenshot-dropzone {
  border: 1.5px dashed var(--mfb-border-strong);
  border-radius: var(--mfb-radius-lg);
  padding: var(--mfb-space-6) var(--mfb-space-4);
  text-align: center;
  cursor: pointer;
  background: var(--mfb-surface);
  transition: border-color 160ms ease, background 160ms ease, transform 120ms ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--mfb-space-2);
}
.screenshot-dropzone:hover {
  border-color: var(--mfb-accent);
  background: color-mix(in srgb, var(--mfb-accent) 4%, var(--mfb-surface));
}
.screenshot-dropzone.is-dragover {
  border-color: var(--mfb-accent);
  border-style: solid;
  background: color-mix(in srgb, var(--mfb-accent) 10%, var(--mfb-surface));
  transform: scale(1.005);
}
.screenshot-dropzone:focus-visible {
  outline: 2px solid var(--mfb-accent);
  outline-offset: 2px;
}
.screenshot-icon {
  color: var(--mfb-text-muted);
  opacity: 0.7;
  margin-bottom: var(--mfb-space-1);
  transition: color 160ms ease, opacity 160ms ease;
}
.screenshot-dropzone:hover .screenshot-icon,
.screenshot-dropzone.is-dragover .screenshot-icon {
  color: var(--mfb-accent);
  opacity: 1;
}
.screenshot-cta { font-size: var(--mfb-text-base); color: var(--mfb-text); font-weight: 500; }
.screenshot-cta strong { color: var(--mfb-accent); font-weight: 600; }
.screenshot-formats { font-size: var(--mfb-text-xs); color: var(--mfb-text-muted); }

.screenshot-preview {
  position: relative;
  border: 1px solid var(--mfb-border);
  border-radius: var(--mfb-radius);
  overflow: hidden;
  background: var(--mfb-surface);
  display: flex;
  flex-direction: column;
}
.screenshot-preview img {
  display: block;
  width: 100%;
  height: auto;
  max-height: 280px;
  object-fit: contain;
  background: #1a1a1a;
}
.screenshot-preview-actions {
  display: flex;
  gap: var(--mfb-space-2);
  padding: var(--mfb-space-2);
  border-top: 1px solid var(--mfb-border);
  background: var(--mfb-bg);
}
.screenshot-preview-actions .btn {
  flex: 1;
  padding: 8px 12px;
  font-size: var(--mfb-text-sm);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.screenshot-preview-actions .btn--primary {
  background: var(--mfb-bg);
  color: var(--mfb-accent);
  border-color: var(--mfb-accent);
}
.screenshot-preview-actions .btn--primary:hover {
  background: color-mix(in srgb, var(--mfb-accent) 8%, var(--mfb-bg));
  border-color: var(--mfb-accent);
  color: var(--mfb-accent);
}
.screenshot-remove {
  /* Kept for legacy markup that uses the old corner-cross button. */
  position: absolute;
  top: 6px;
  right: 6px;
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid var(--mfb-border);
  border-radius: 999px;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  color: #111827;
}
.screenshot-remove:hover { background: #fff; }
.screenshot-annotate {
  /* Kept for legacy; new markup uses .screenshot-preview-actions. */
  border-radius: 0;
  border-width: 0;
  border-top: 1px solid var(--mfb-border);
  background: var(--mfb-bg);
}
.screenshot-annotate:hover { background: var(--mfb-surface); }

.page-context {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: var(--mfb-text-muted);
}
.page-context-label {
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.04em;
  font-size: 10px;
}
.page-context-url {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  color: var(--mfb-text-muted);
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Annotator modal — sits above the feedback modal (z-index +1). */

.annotator-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.78);
  display: grid;
  place-items: center;
  z-index: 1;
  padding: 12px;
}
.annotator {
  position: relative;
  background: var(--mfb-bg);
  color: var(--mfb-text);
  border-radius: calc(var(--mfb-radius) * 1.5);
  width: min(960px, 96vw);
  max-height: calc(100vh - 24px);
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.4);
}
.annotator-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid var(--mfb-border);
  font-size: 13px;
  font-weight: 600;
}
.annotator-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-bottom: 1px solid var(--mfb-border);
}
.annotator-tools, .annotator-colors { display: flex; gap: 4px; }
.annotator-sep {
  display: inline-block;
  width: 1px;
  height: 18px;
  background: var(--mfb-border);
  margin: 0 4px;
}
.annotator-spacer { flex: 1; }
.annotator-tool {
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  background: var(--mfb-bg);
  color: var(--mfb-text);
  border: 1px solid var(--mfb-border);
  border-radius: var(--mfb-radius);
  cursor: pointer;
}
.annotator-tool:hover { background: var(--mfb-surface); }
.annotator-tool.is-active {
  background: var(--mfb-text);
  color: var(--mfb-bg);
  border-color: var(--mfb-text);
}
.annotator-color {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  border: 2px solid var(--mfb-border);
  cursor: pointer;
  padding: 0;
  transition: transform 120ms ease;
  position: relative;
}
.annotator-color.is-active {
  transform: scale(1.12);
  border-color: var(--mfb-text);
}
/* Color-picker swatch: rainbow gradient fill so users see this isn't
   a preset, plus a tiny native <input type="color"> overlaid invisibly. */
.annotator-color-picker {
  display: grid;
  place-items: center;
  background: conic-gradient(from 180deg, #ef4444, #f59e0b, #10b981, #3b82f6, #8b5cf6, #ec4899, #ef4444);
  overflow: hidden;
}
.annotator-color-picker input[type="color"] {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  padding: 0;
  margin: 0;
  opacity: 0;
  cursor: pointer;
}
.annotator-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 30px;
  padding: 0 10px;
  font: inherit;
  font-size: 12px;
  background: var(--mfb-bg);
  color: var(--mfb-text);
  border: 1px solid var(--mfb-border);
  border-radius: var(--mfb-radius);
  cursor: pointer;
}
.annotator-btn:hover { background: var(--mfb-surface); }
.annotator-btn[disabled] { opacity: 0.5; cursor: not-allowed; }
.annotator-count { font-size: 11px; color: var(--mfb-text-muted); }
.annotator-canvas-wrap {
  flex: 1;
  overflow: auto;
  background: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  min-height: 200px;
}
.annotator-canvas {
  cursor: crosshair;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.45);
  background: #fff;
}
.annotator-loading {
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
}
.annotator-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid var(--mfb-border);
}

/* ---- v0.7: tabs + reader UI -------------------------------------- */

.tab-strip {
  display: flex;
  gap: var(--mfb-space-1);
  border-bottom: 1px solid var(--mfb-border);
  margin: 0;
  padding: 0;
}
.tab-button {
  appearance: none;
  background: transparent;
  border: 0;
  border-bottom: 2px solid transparent;
  padding: var(--mfb-space-3) var(--mfb-space-4);
  margin-bottom: -1px;
  font: inherit;
  font-size: var(--mfb-text-sm);
  font-weight: 500;
  color: var(--mfb-text-muted);
  cursor: pointer;
  transition: color 120ms ease, border-color 120ms ease;
}
.tab-button:hover { color: var(--mfb-text); }
.tab-button.is-active {
  color: var(--mfb-accent);
  border-bottom-color: var(--mfb-accent);
}
.tab-button[aria-selected="true"] { font-weight: 600; }

.mine-list {
  display: flex;
  flex-direction: column;
  gap: var(--mfb-space-3);
  /* Matches .board-view's entrance so switching tabs feels like a real
   * page transition rather than a flicker. */
  animation: mfb-board-in 280ms ease-out 40ms both;
}
@media (prefers-reduced-motion: reduce) {
  .mine-list { animation: none; }
}
.mine-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.mine-list-header h2 { margin: 0; font-size: var(--mfb-text-lg); font-weight: 600; letter-spacing: -0.01em; }
.mine-loading { color: var(--mfb-text-muted); font-size: 13px; }
.mine-empty {
  text-align: center;
  padding: 24px 12px;
  color: var(--mfb-text-muted);
  font-size: 13px;
}
.mine-empty strong { display: block; color: var(--mfb-text); margin-bottom: 4px; }

.mine-rows {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 380px;
  overflow-y: auto;
}
.mine-row {
  appearance: none;
  text-align: left;
  background: var(--mfb-surface);
  border: 1px solid var(--mfb-border);
  border-radius: var(--mfb-radius);
  padding: 10px 12px;
  font: inherit;
  color: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  transition:
    border-color 140ms ease,
    background 140ms ease,
    transform 100ms ease,
    box-shadow 140ms ease;
}
.mine-row:hover {
  border-color: var(--mfb-border-strong);
  background: var(--mfb-bg);
  transform: translateY(-1px);
  box-shadow: var(--mfb-shadow);
}
.mine-row:active { transform: scale(0.997); }
.mine-row:focus-visible {
  outline: 2px solid var(--mfb-accent);
  outline-offset: 2px;
}
.mine-row-pills { display: flex; gap: 4px; flex-wrap: wrap; }
.mine-row-preview {
  font-size: 13px;
  color: var(--mfb-text);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.mine-row-meta {
  display: flex;
  gap: 6px;
  font-size: 11px;
  color: var(--mfb-text-muted);
}

.pill {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid transparent;
}
.pill-status { background: #eff6ff; color: #1e40af; border-color: #dbeafe; }
.pill-status--in_progress { background: #fffbeb; color: #92400e; border-color: #fde68a; }
.pill-status--awaiting_validation { background: #faf5ff; color: #6b21a8; border-color: #e9d5ff; }
.pill-status--closed { background: #ecfdf5; color: #065f46; border-color: #a7f3d0; }
.pill-status--rejected, .pill-status--wontfix, .pill-status--duplicate { background: #f3f4f6; color: #374151; border-color: #e5e7eb; }
.pill-type { background: var(--mfb-surface); color: var(--mfb-text-muted); border-color: var(--mfb-border); }
.pill-severity { background: var(--mfb-surface); color: var(--mfb-text-muted); border-color: var(--mfb-border); }
.pill-severity--blocker { background: #fef2f2; color: #991b1b; border-color: #fecaca; }
.pill-severity--high { background: #fff7ed; color: #9a3412; border-color: #fed7aa; }
.pill-severity--medium { background: #fefce8; color: #854d0e; border-color: #fef08a; }
.pill-severity--low { background: var(--mfb-surface); color: var(--mfb-text-muted); }

.report-detail { display: flex; flex-direction: column; gap: 12px; }
.report-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.report-detail-body { display: flex; flex-direction: column; gap: 10px; }
.report-detail-description {
  font-size: 14px;
  white-space: pre-wrap;
  margin: 0;
}
.report-detail-screenshot {
  display: block;
  border: 1px solid var(--mfb-border);
  border-radius: var(--mfb-radius);
  overflow: hidden;
  background: #1a1a1a;
}
.report-detail-screenshot img {
  display: block;
  width: 100%;
  max-height: 200px;
  object-fit: contain;
}
.report-detail-section {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--mfb-text-muted);
  margin: 8px 0 4px;
  font-weight: 600;
}

.report-detail-context {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  background: var(--mfb-surface);
  border-radius: var(--mfb-radius);
  border: 1px solid var(--mfb-border);
}
.report-detail-context-pills { display: flex; gap: 4px; flex-wrap: wrap; }
.report-detail-context-line {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 12px;
  color: var(--mfb-text);
}
.report-detail-context-label {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 10px;
  font-weight: 600;
  color: var(--mfb-text-muted);
  min-width: 56px;
}
.report-detail-context-url {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: var(--mfb-accent);
  text-decoration: none;
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.report-detail-context-url:hover { text-decoration: underline; }
.pill-capture { background: var(--mfb-bg); color: var(--mfb-text-muted); border-color: var(--mfb-border); }
.report-detail-empty {
  font-size: 12px;
  color: var(--mfb-text-muted);
  margin: 0;
}

.report-comments {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 300px;
  overflow-y: auto;
}

.comment-bubble {
  padding: 8px 10px;
  border-radius: 12px;
  max-width: 88%;
  font-size: 13px;
  line-height: 1.4;
}
.comment-bubble.is-mine {
  align-self: flex-end;
  background: var(--mfb-accent);
  color: var(--mfb-accent-contrast);
}
.comment-bubble.is-other {
  align-self: flex-start;
  background: var(--mfb-surface);
  border: 1px solid var(--mfb-border);
  color: var(--mfb-text);
}
.comment-author {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  margin-bottom: 2px;
}
.comment-author--mcp { color: #6b21a8; }
.comment-author--staff { color: #1e40af; }
.comment-author--system { color: var(--mfb-text-muted); }
.comment-body { white-space: pre-wrap; }
.comment-time {
  font-size: 10px;
  margin-top: 4px;
  opacity: 0.7;
}

.report-compose {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
}
.report-compose textarea {
  font: inherit;
  font-size: 13px;
  padding: 8px 10px;
  border: 1px solid var(--mfb-border);
  border-radius: var(--mfb-radius);
  background: var(--mfb-surface);
  color: inherit;
  min-height: 64px;
  resize: vertical;
}
.report-compose-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

/* v0.15.3 — teammate-viewing notice. Shown in place of the compose
 * box when a non-submitter opens a project-wide row from the Board
 * tab. Replies are private to the submitter; we surface this once
 * (calm tone, neutral surface) so the viewer understands why no
 * Reply box appears, instead of leaving them wondering. */
.report-detail-teammate-notice {
  margin: 0;
  padding: var(--mfb-space-3) var(--mfb-space-4);
  background: var(--mfb-surface);
  border: 1px dashed var(--mfb-border);
  border-radius: var(--mfb-radius);
  font-size: var(--mfb-text-sm);
  color: var(--mfb-text-muted);
  font-style: italic;
}

/* v0.15.3 — friendly "report not available" empty state. Replaces
 * the raw API error message (e.g. getReport failed: 404 …) that
 * used to leak from setError(err.message) straight into the DOM. */
.report-detail-empty-state {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--mfb-space-3);
  padding: var(--mfb-space-5);
  background: var(--mfb-surface);
  border-radius: var(--mfb-radius);
  border: 1px solid var(--mfb-border);
}
.report-detail-empty-state-title {
  margin: 0;
  font-size: var(--mfb-text-md);
  font-weight: 600;
  color: var(--mfb-text);
}
.report-detail-empty-state-body {
  margin: 0;
  font-size: var(--mfb-text-sm);
  color: var(--mfb-text-muted);
}

/* Status history — read-only audit trail visible to the submitter.
   Timeline of who flipped the status and when (operator vs. agent vs.
   system). Bordered, neutral surface so it doesn't compete visually
   with the conversation thread above. */
.report-detail-history {
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid var(--mfb-border);
  border-radius: var(--mfb-radius);
  background: var(--mfb-surface);
  padding: 8px 10px;
}
.report-detail-history .report-detail-section {
  margin: 0;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--mfb-text-muted);
}
.status-history {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.status-history-row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 8px;
  align-items: center;
  font-size: 11px;
}
.status-history-time {
  color: var(--mfb-text-muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.status-history-transition {
  display: inline-flex;
  gap: 4px;
  align-items: center;
  flex-wrap: wrap;
}
.status-history-arrow {
  color: var(--mfb-text-muted);
  font-size: 11px;
}
.status-history-source {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  white-space: nowrap;
}
.status-history-source--mcp { color: #6b21a8; }
.status-history-source--user { color: #1e40af; }
.status-history-source--system { color: var(--mfb-text-muted); }

/* Transparency drawer — closed by default. The user can click open to
   verify what their browser sent: device, errors, console tail, network
   tail. Read-only; purely a trust-building gesture. */
.report-detail-tech {
  border: 1px solid var(--mfb-border);
  border-radius: var(--mfb-radius);
  background: var(--mfb-surface);
}
.report-detail-tech > summary {
  cursor: pointer;
  padding: 8px 10px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--mfb-text-muted);
  user-select: none;
  list-style: none;
}
.report-detail-tech > summary::-webkit-details-marker { display: none; }
.report-detail-tech > summary::before {
  content: '▸';
  display: inline-block;
  margin-right: 6px;
  transition: transform 120ms;
}
.report-detail-tech[open] > summary::before { transform: rotate(90deg); }
.tech-body {
  padding: 0 10px 10px 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.tech-section h4 {
  margin: 0 0 4px 0;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--mfb-text-muted);
  font-weight: 600;
}
.tech-device {
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 8px;
  row-gap: 2px;
  margin: 0;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}
.tech-device dt { color: var(--mfb-text-muted); }
.tech-device dd { margin: 0; }
.tech-errors, .tech-console, .tech-network {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 11px;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  max-height: 200px;
  overflow-y: auto;
}
.tech-errors li { padding: 4px 0; border-top: 1px solid var(--mfb-border); }
.tech-errors li:first-child { border-top: 0; }
.tech-errors-msg { color: #991b1b; font-weight: 600; }
.tech-errors-stack {
  margin: 4px 0 0 0;
  padding: 4px 6px;
  background: var(--mfb-bg);
  border-radius: 4px;
  font-size: 10px;
  white-space: pre-wrap;
  color: var(--mfb-text-muted);
  max-height: 120px;
  overflow-y: auto;
}
.tech-console-row {
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 6px;
  padding: 1px 0;
}
.tech-console-level {
  text-transform: uppercase;
  font-size: 9px;
  font-weight: 600;
  align-self: center;
  color: var(--mfb-text-muted);
}
.tech-console-row--warn .tech-console-level { color: #92400e; }
.tech-console-row--error .tech-console-level { color: #991b1b; }
.tech-console-row--info .tech-console-level { color: #1e40af; }
.tech-console-msg { word-break: break-word; }
.tech-network-row {
  display: grid;
  grid-template-columns: 48px 56px 1fr 56px;
  gap: 6px;
  padding: 1px 0;
}
.tech-network-row--fail .tech-network-status { color: #991b1b; font-weight: 700; }
.tech-network-status { font-variant-numeric: tabular-nums; }
.tech-network-method { color: var(--mfb-text-muted); }
.tech-network-url { word-break: break-all; }
.tech-network-time { text-align: right; color: var(--mfb-text-muted); font-variant-numeric: tabular-nums; }

/* KPI strip — four pills above the My Reports list. Each is clickable
   to filter the rows below. Borrowed from thePnr's FeedbackKPICards. */
.kpi-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}
.kpi-cell {
  appearance: none;
  background: var(--mfb-bg);
  border: 1px solid var(--mfb-border);
  border-radius: var(--mfb-radius);
  padding: 10px 10px 10px 14px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  font-family: inherit;
  color: inherit;
  position: relative;
  overflow: hidden;
  transition:
    background 140ms ease,
    border-color 140ms ease,
    transform 100ms ease,
    box-shadow 140ms ease;
}
.kpi-cell::before {
  /* Vertical tone accent — matches the Board KPI cards' visual rhythm so
   * the two surfaces feel like they belong to the same product. v0.12
   * polish pass. */
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 3px;
  height: 100%;
  background: var(--mfb-border);
  transition: background 140ms ease;
}
.kpi-cell--new::before { background: #3b82f6; }
.kpi-cell--in_progress::before { background: #f59e0b; }
.kpi-cell--awaiting_validation::before { background: #a855f7; }
.kpi-cell--resolved::before { background: #10b981; }
.kpi-cell:hover {
  background: var(--mfb-surface);
  transform: translateY(-1px);
  box-shadow: var(--mfb-shadow);
}
.kpi-cell.is-active {
  border-color: var(--mfb-accent);
  background: rgba(59, 130, 246, 0.08);
}
.kpi-cell.is-active.kpi-cell--in_progress { border-color: #d97706; background: rgba(251, 191, 36, 0.10); }
.kpi-cell.is-active.kpi-cell--awaiting_validation { border-color: #9333ea; background: rgba(168, 85, 247, 0.10); }
.kpi-cell.is-active.kpi-cell--resolved { border-color: #059669; background: rgba(16, 185, 129, 0.10); }
.kpi-value {
  font-size: 18px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.kpi-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--mfb-text-muted);
}

/* Changelog ("This week") — week-grouped resolved-report timeline.
   Borrowed from thePnr's FeedbackChangelogView; the header per group
   reads "● Week of {date}  ━━━  {n} resolved". */
.changelog-groups {
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: mfb-board-in 280ms ease-out 40ms both;
}
@media (prefers-reduced-motion: reduce) {
  .changelog-groups { animation: none; }
}
.changelog-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.changelog-group-header {
  display: grid;
  grid-template-columns: auto auto 1fr auto;
  gap: 6px;
  align-items: center;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--mfb-text-muted);
}
.changelog-group-marker {
  color: var(--mfb-accent);
  font-size: 14px;
  line-height: 1;
}
.changelog-group-label { font-weight: 600; }
.changelog-group-rule {
  height: 1px;
  background: var(--mfb-border);
  align-self: center;
}
.changelog-group-count {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

/* ---- v0.12: Board tab + expanded modal ("transport" feel) ------------ */

/* When the Board tab is active, the modal grows toward near-fullscreen
 * on desktop and full-screen on mobile. The CSS transition gives the
 * sense of clicking through to a real page rather than switching tabs.
 * Width/height/max-* are animated together; padding too, since the
 * board's denser layout needs less of the modal's default padding. */
.modal.is-expanded {
  /* Margin around the expanded board view. --mfb-space-7 (48px) matches
   * the breathing room of the default modal — 24px top + 24px bottom +
   * 24px on each side — so the panel reads as "near-fullscreen with a
   * visible frame" instead of "the whole viewport, content cut off".
   *
   * The base .modal rule uses the browser default content-box, so
   * without an override the 24px padding would be ADDED to the
   * calc(100vh - 48px) height — the rendered box would exactly equal
   * the viewport again, defeating the margin. Force border-box so
   * padding lives INSIDE the height we set. */
  box-sizing: border-box;
  width: min(1280px, calc(100vw - var(--mfb-space-7)));
  max-height: calc(100vh - var(--mfb-space-7));
  height: calc(100vh - var(--mfb-space-7));
  padding: var(--mfb-space-5);
  transition:
    width 320ms cubic-bezier(0.22, 1, 0.36, 1),
    height 320ms cubic-bezier(0.22, 1, 0.36, 1),
    max-height 320ms cubic-bezier(0.22, 1, 0.36, 1),
    padding 320ms cubic-bezier(0.22, 1, 0.36, 1);
}
@media (prefers-reduced-motion: reduce) {
  .modal.is-expanded { transition: none; }
}
.backdrop.is-expanded { /* hook for any backdrop-level overrides */ }

@media (max-width: 640px) {
  /* On mobile the sheet still snaps to the bottom edge and stretches
   * almost the full viewport — 24px gap from the top is enough to
   * convey "this is a panel, not a takeover", but we don't pull margin
   * off the sides where users expect the sheet to fill the viewport. */
  .modal.is-expanded {
    width: 100vw;
    height: calc(100vh - var(--mfb-space-5));
    border-radius: var(--mfb-radius-lg) var(--mfb-radius-lg) 0 0;
  }
}

/* Decorative purple accent on the Board tab button so users discover
 * it's a different surface — subtle, not loud. */
.tab-button--board.is-active {
  color: #6b21a8;
  box-shadow: inset 0 -2px 0 0 #a855f7;
}
.tab-button--board:not(.is-active):hover {
  color: #6b21a8;
}

/* ---- Board layout ---- */

.board-view {
  display: flex;
  flex-direction: column;
  gap: var(--mfb-space-4);
  flex: 1 1 auto;
  min-height: 0; /* allow inner panes to scroll */
  animation: mfb-board-in 280ms ease-out 40ms both;
}
@keyframes mfb-board-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .board-view { animation: none; }
}

.board-header {
  display: flex;
  flex-direction: column;
  gap: var(--mfb-space-4);
}
.board-header-title {
  display: flex;
  align-items: center;
  gap: 12px;
}
.board-header-emoji {
  font-size: 26px;
  line-height: 1;
}
.board-header-h {
  margin: 0;
  font-size: var(--mfb-text-2xl);
  font-weight: 600;
  letter-spacing: -0.01em;
}
.board-header-sub {
  margin: 2px 0 0 0;
  color: var(--mfb-text-muted);
  font-size: var(--mfb-text-sm);
}

/* KPI strip — 4 cards, equal width, subtle tone-tints by bucket. */
.board-kpi-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}
.board-kpi-card {
  border: 1px solid var(--mfb-border);
  border-radius: var(--mfb-radius);
  padding: 14px 16px;
  background: var(--mfb-bg);
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  overflow: hidden;
  transition: transform 160ms ease, box-shadow 160ms ease;
}
.board-kpi-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 3px;
  height: 100%;
  background: var(--mfb-border);
  transition: background 160ms ease;
}
.board-kpi-card:hover {
  transform: translateY(-1px);
  box-shadow: var(--mfb-shadow);
}
.board-kpi-card.tone-new::after { background: #3b82f6; }
.board-kpi-card.tone-progress::after { background: #f59e0b; }
.board-kpi-card.tone-validation::after { background: #a855f7; }
.board-kpi-card.tone-rate::after { background: #10b981; }
.board-kpi-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.02em;
  color: var(--mfb-text);
  font-variant-numeric: tabular-nums;
}
.board-kpi-label {
  font-size: var(--mfb-text-xs);
  color: var(--mfb-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 500;
}
.board-kpi-strip.is-loading .board-kpi-value {
  opacity: 0.35;
}
@media (max-width: 720px) {
  .board-kpi-strip { grid-template-columns: repeat(2, 1fr); }
}

/* Filter row */
.board-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.board-filter-select,
.board-filter-search {
  padding: 6px 10px;
  border: 1px solid var(--mfb-border);
  border-radius: var(--mfb-radius);
  background: var(--mfb-bg);
  color: var(--mfb-text);
  font: inherit;
  font-size: var(--mfb-text-sm);
  height: 32px;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.board-filter-select:focus-visible,
.board-filter-search:focus-visible {
  outline: none;
  border-color: var(--mfb-accent);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--mfb-accent) 18%, transparent);
}
.board-filter-search { min-width: 180px; flex: 1 1 220px; }
.board-filter-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--mfb-text-sm);
  color: var(--mfb-text);
  cursor: pointer;
  user-select: none;
}
.board-filter-toggle input { margin: 0; }
.board-filter-clear {
  background: transparent;
  border: 0;
  color: var(--mfb-text-muted);
  font: inherit;
  font-size: var(--mfb-text-sm);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: var(--mfb-radius);
  transition: background 120ms ease, color 120ms ease;
}
.board-filter-clear:hover {
  color: var(--mfb-text);
  background: var(--mfb-surface);
}

/* Master/detail layout — two-column at >=900px, stacked below. */
.board-body {
  display: grid;
  grid-template-columns: minmax(280px, 360px) 1fr;
  gap: var(--mfb-space-4);
  flex: 1 1 auto;
  min-height: 0;
}
@media (max-width: 900px) {
  .board-body { grid-template-columns: 1fr; }
  .board-detail-wrap:not(.has-selection) { display: none; }
}

.board-list-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 0;
  overflow: hidden;
}
.board-list-count {
  font-size: var(--mfb-text-xs);
  color: var(--mfb-text-muted);
  padding: 0 4px;
}
.board-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  flex: 1 1 auto;
  scrollbar-width: thin;
  scrollbar-gutter: stable;
}

.board-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  text-align: left;
  padding: 12px 14px;
  border-radius: var(--mfb-radius);
  background: var(--mfb-bg);
  border: 1px solid var(--mfb-border);
  cursor: pointer;
  font: inherit;
  color: inherit;
  /* Stagger reveal on first paint. */
  animation: mfb-row-in 280ms ease-out both;
  transition: border-color 140ms ease, background 140ms ease, transform 80ms ease;
}
.board-row:hover {
  border-color: var(--mfb-border-strong);
  background: var(--mfb-surface);
}
.board-row:active { transform: scale(0.997); }
.board-row.is-selected {
  border-color: var(--mfb-accent);
  background: color-mix(in srgb, var(--mfb-accent) 6%, var(--mfb-bg));
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--mfb-accent) 18%, transparent);
}
@keyframes mfb-row-in {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .board-row { animation: none; }
}
/* Stagger first 6 rows so the list reveals top-down. */
.board-row:nth-child(1) { animation-delay: 0ms; }
.board-row:nth-child(2) { animation-delay: 40ms; }
.board-row:nth-child(3) { animation-delay: 80ms; }
.board-row:nth-child(4) { animation-delay: 120ms; }
.board-row:nth-child(5) { animation-delay: 160ms; }
.board-row:nth-child(6) { animation-delay: 200ms; }

.board-row-badges {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
}
.board-row-description {
  font-size: var(--mfb-text-sm);
  color: var(--mfb-text);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.board-row-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: var(--mfb-text-xs);
  color: var(--mfb-text-muted);
  flex-wrap: wrap;
}
.board-row-author { font-weight: 500; color: var(--mfb-text); }
.board-row-comments {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-variant-numeric: tabular-nums;
}
.board-row-time { margin-left: auto; font-variant-numeric: tabular-nums; }

/* Status / severity badges shared with the detail panel. The tones
 * borrow PNR's badge palette — calm pastels that don't fight content. */
.board-row-badges .badge {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.02em;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  text-transform: lowercase;
}
.badge.status-new { background: #dbeafe; color: #1e40af; }
.badge.status-in_progress { background: #fef3c7; color: #92400e; }
.badge.status-awaiting_validation { background: #ede9fe; color: #6b21a8; }
.badge.status-closed { background: #d1fae5; color: #065f46; }
.badge.status-rejected { background: #fee2e2; color: #991b1b; }
.badge.status-duplicate { background: #f3f4f6; color: #4b5563; }
.badge.status-wontfix { background: #f3f4f6; color: #4b5563; }
.badge.severity-blocker { background: #fee2e2; color: #991b1b; }
.badge.severity-high { background: #fee2e2; color: #b91c1c; }
.badge.severity-medium { background: #fef3c7; color: #92400e; }
.badge.severity-low { background: #e0f2fe; color: #075985; }

/* Detail pane (within Board). Inherits from .report-detail.variant-board */
.board-detail-wrap {
  border: 1px solid var(--mfb-border);
  border-radius: var(--mfb-radius);
  background: var(--mfb-bg);
  overflow-y: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.board-detail-empty {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--mfb-text-muted);
  padding: var(--mfb-space-5);
  text-align: center;
  font-size: var(--mfb-text-sm);
}
.board-detail-empty svg { opacity: 0.6; }

.report-detail.variant-board { padding: 16px 18px; }
.report-detail-header--board {
  align-items: center;
}
.report-detail-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 10px;
}

/* Empty + loading + error states */
.board-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  padding: var(--mfb-space-5);
  color: var(--mfb-text-muted);
  flex: 1 1 auto;
}
.board-empty h3 {
  margin: 0;
  font-size: var(--mfb-text-base);
  color: var(--mfb-text);
}
.board-empty p {
  margin: 0;
  font-size: var(--mfb-text-sm);
}
.board-error {
  padding: 12px 14px;
  border-radius: var(--mfb-radius);
  background: #fef2f2;
  color: #991b1b;
  font-size: var(--mfb-text-sm);
}

/* Skeleton rows pulse subtly so the user knows something's coming. */
.board-list-skeleton .skeleton-row {
  cursor: default;
  pointer-events: none;
}
.skeleton-line {
  height: 12px;
  background: linear-gradient(
    90deg,
    var(--mfb-surface) 0%,
    color-mix(in srgb, var(--mfb-surface) 60%, var(--mfb-border)) 50%,
    var(--mfb-surface) 100%
  );
  background-size: 200% 100%;
  border-radius: 4px;
  animation: mfb-skeleton 1400ms ease-in-out infinite;
}
.skeleton-badges { width: 60%; height: 14px; }
.skeleton-text { width: 90%; }
.skeleton-text.short { width: 60%; }
@keyframes mfb-skeleton {
  from { background-position: 100% 0; }
  to   { background-position: -100% 0; }
}
@media (prefers-reduced-motion: reduce) {
  .skeleton-line { animation: none; }
}
`
